import React, { useState } from 'react';
import { ArrowLeft, Camera, FileText, FileUp, CheckCircle } from 'lucide-react';
import { useApp } from '@/context/AppContext';

export default function UploadStatementScreen() {
  const { goBack, navigate } = useApp();
  const [phase, setPhase] = useState('idle'); // idle | uploading | success

  const handleUpload = () => {
    setPhase('uploading');
    setTimeout(() => setPhase('success'), 2200);
  };

  /* ── Uploading state ── */
  if (phase === 'uploading') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-8">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ background: '#FFF0EB', animation: 'pulse 1.4s ease-in-out infinite' }}
        >
          <FileUp size={36} color="#FF5722" />
        </div>
        <div className="font-black text-gray-800 text-lg mb-2">Uploading...</div>
        <div className="text-gray-400 text-sm">Processing your invoice</div>
        <div className="mt-8 w-48 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#FF5722] rounded-full"
            style={{ animation: 'progress-fill 2.2s ease-in-out forwards' }}
          />
        </div>
        <style>{`
          @keyframes progress-fill {
            from { width: 0% }
            to { width: 100% }
          }
        `}</style>
      </div>
    );
  }

  /* ── Success state ── */
  if (phase === 'success') {
    return (
      <div
        data-testid="upload-statement-success"
        className="min-h-screen bg-white flex flex-col items-center justify-center px-8"
      >
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
          style={{ background: '#F0FDF4' }}
        >
          <CheckCircle size={52} color="#22C55E" strokeWidth={1.8} />
        </div>
        <h1 className="font-black text-2xl text-gray-900 text-center leading-snug mb-3">
          Invoice Uploaded<br />Successfully!
        </h1>
        <p className="text-gray-400 text-sm text-center mb-12">
          Your invoice has been submitted for review
        </p>
        <button
          data-testid="upload-success-view-pending-btn"
          onClick={() => navigate('PENDING_INVOICES')}
          className="w-full text-white font-black py-4 rounded-2xl tap-scale text-base"
          style={{ background: 'linear-gradient(to right, #FF7043, #FF5722)' }}
        >
          View Pending Invoices
        </button>
        <button
          onClick={() => navigate('HOME')}
          className="mt-3 w-full text-gray-500 font-semibold py-3 tap-scale text-sm"
        >
          Back to Home
        </button>
      </div>
    );
  }

  /* ── Idle state ── */
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <div className="bg-white px-4 pt-5 pb-3 border-b border-gray-100 flex items-center">
        <button
          data-testid="upload-statement-back"
          onClick={goBack}
          className="w-9 h-9 flex items-center justify-center tap-scale"
        >
          <ArrowLeft size={22} color="#374151" />
        </button>
        <h1
          className="font-black text-gray-900 text-center flex-1 uppercase"
          style={{ fontSize: '15px', letterSpacing: '0.1em' }}
        >
          UPLOAD INVOICE
        </h1>
        <div className="w-9 h-9" />
      </div>

      <div className="px-4 pt-5 pb-8 flex flex-col gap-4">
        {/* Page title */}
        <div>
          <h2 className="font-black text-xl text-gray-900 mb-1">Upload Invoice</h2>
          <p className="text-gray-400 text-sm leading-snug">
            Upload your distributor invoice to keep your records up to date
          </p>
        </div>

        {/* Upload card — dashed border */}
        <div
          className="bg-white rounded-2xl p-5"
          style={{ border: '1.5px dashed #D1D5DB' }}
        >
          {/* Icon + title */}
          <div className="flex flex-col items-center mb-5 pt-2">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: '#FFF0EB' }}
            >
              <FileUp size={30} color="#FF5722" />
            </div>
            <h3 className="font-black text-lg text-gray-900 mb-1">Upload Invoice</h3>
            <p className="text-gray-400 text-sm text-center">
              Choose a method to upload your invoice
            </p>
          </div>

          {/* Camera + File buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="upload-camera-btn"
              onClick={handleUpload}
              className="rounded-2xl py-5 flex flex-col items-center gap-2 tap-scale"
              style={{ background: '#FFF0EB' }}
            >
              <Camera size={28} color="#FF5722" />
              <span className="font-bold text-sm" style={{ color: '#FF5722' }}>Camera</span>
            </button>
            <button
              data-testid="upload-file-btn"
              onClick={handleUpload}
              className="rounded-2xl py-5 flex flex-col items-center gap-2 tap-scale"
              style={{ background: '#FFF0EB' }}
            >
              <FileText size={28} color="#FF5722" />
              <span className="font-bold text-sm" style={{ color: '#FF5722' }}>File</span>
            </button>
          </div>
        </div>

        {/* No invoices yet card */}
        <div className="bg-white rounded-2xl p-6 flex flex-col items-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{ background: '#FFF0EB' }}
          >
            <FileUp size={28} color="#FF5722" />
          </div>
          <h3 className="font-black text-lg text-gray-900 mb-2">No Invoices Yet</h3>
          <p className="text-gray-400 text-sm text-center leading-relaxed">
            Upload your first invoice to keep your records up to date and track pending amounts
          </p>
        </div>
      </div>
    </div>
  );
}
